import { isEmpty } from '@fruitful/fruitils';
import {
    type AiAgentId,
    CONVERSATIONAL_MM_AGENT,
    getBaseResourceId,
    getSproutStreamSelectionLabels,
    SPROUT_AGENT,
} from '@fruitful/llm-service-shared';
import type { ThreadError } from '@fruitful/types';

import type { UserCategory } from '../../entities/entityTypes';
import { useGetUserByStytchId } from '../../member/hooks/useGetUserByStytchId';
import {
    getThreadConversationStatus,
    getThreadErrorTypes,
    type ThreadConversationStatus,
} from '../utils/threadMetadataUtils';
import { useGetAdminMastraThread } from './useGetAdminMastraThread';
import { useGetMastraThread } from './useGetMastraThread';
import { useLiveThreadRefetchInterval } from './useLiveThreadRefetchInterval';

interface ThreadDetailsView {
    title: string | null;
    resourceId: string | null;
    visitorId: string | null;
    userId: string | null;
    membershipId: string | null;
    userCategory: UserCategory | null;
    logRocketUserId: string | null;
    mixpanelUserId: string | null;
    status: ThreadConversationStatus | null;
    errors: ThreadError[];
    model: string | null;
    reasoningEffort: string | null;
}

const EMPTY_THREAD_DETAILS: ThreadDetailsView = {
    title: null,
    resourceId: null,
    visitorId: null,
    userId: null,
    membershipId: null,
    userCategory: null,
    logRocketUserId: null,
    mixpanelUserId: null,
    status: null,
    errors: [],
    model: null,
    reasoningEffort: null,
};

const isCmmAgent = (agentId: AiAgentId | undefined): boolean =>
    agentId === CONVERSATIONAL_MM_AGENT.id;

const toMixpanelUserId = (
    userId: string | null,
    visitorId: string | null,
): string | null => [userId, visitorId].find((id) => !isEmpty(id)) ?? null;

const toBaseResourceId = (
    resourceId: string | null,
    agentId: AiAgentId | undefined,
): string | null => {
    if (resourceId == null || agentId == null) {
        return null;
    }

    return getBaseResourceId(resourceId, agentId);
};

const toThreadTitle = (
    mastraThread: { title?: string | null } | null | undefined,
): string | null => {
    const trimmed = mastraThread?.title?.trim();
    if (trimmed == null || trimmed === '') {
        return null;
    }

    return trimmed;
};

export const useThreadDetails = ({
    threadId,
    agentId,
    isRunningEval,
}: {
    threadId?: string;
    agentId?: AiAgentId;
    isRunningEval: boolean;
}): ThreadDetailsView => {
    const refetchInterval = useLiveThreadRefetchInterval({
        additionalShouldPoll: isRunningEval,
    });
    const { data: mastraThread } = useGetMastraThread({
        threadId,
        agentId,
        refetchInterval,
    });
    const resourceId = mastraThread?.resourceId ?? null;
    const title = toThreadTitle(mastraThread);
    const baseResourceId = toBaseResourceId(resourceId, agentId);
    const shouldLookupSproutUser =
        agentId === SPROUT_AGENT.id && !isEmpty(baseResourceId);

    const { data: adminThread } = useGetAdminMastraThread({
        mastraThreadId:
            isCmmAgent(agentId) && !isEmpty(threadId) ? threadId : undefined,
    });
    const { data: user } = useGetUserByStytchId(
        { stytchId: shouldLookupSproutUser ? (baseResourceId ?? '') : '' },
        { enabled: shouldLookupSproutUser },
    );

    if (agentId == null) {
        return { ...EMPTY_THREAD_DETAILS, resourceId, title };
    }

    const status = isCmmAgent(agentId)
        ? getThreadConversationStatus(
              mastraThread?.metadata,
              mastraThread?.updatedAt,
          )
        : null;
    const errors = getThreadErrorTypes(mastraThread?.metadata);
    const { model, reasoningEffort } = getSproutStreamSelectionLabels(
        mastraThread?.metadata,
    );

    switch (agentId) {
        case SPROUT_AGENT.id: {
            const userId = user?.id ?? null;
            return {
                title,
                resourceId,
                status,
                errors,
                model,
                reasoningEffort,
                visitorId: null,
                userId,
                membershipId: user?.membershipId ?? null,
                userCategory: user?.category ?? null,
                logRocketUserId: userId,
                mixpanelUserId: toMixpanelUserId(userId, null),
            };
        }
        case CONVERSATIONAL_MM_AGENT.id: {
            const userId = adminThread?.userId ?? null;
            return {
                title,
                resourceId,
                status,
                errors,
                model: null,
                reasoningEffort: null,
                visitorId: baseResourceId,
                userId,
                membershipId: adminThread?.membershipId ?? null,
                userCategory: null,
                logRocketUserId: baseResourceId,
                mixpanelUserId: toMixpanelUserId(userId, baseResourceId),
            };
        }
        default: {
            const _exhaustive: never = agentId;
            return _exhaustive;
        }
    }
};
